import { useMutation, useQuery } from '@tanstack/react-query';
import { Checkbox, Form } from 'antd';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Filter } from '../../../@types/filter';
import { confirmationApi } from '../../../apis';
import {
  BatchUpdateConfirmationDto,
  BatchUpdateConfirmationDtoStatusEnum,
  EmployeeRelatedInformationDtoRolesEnum,
  UpdateConfirmationDto,
  UpdateConfirmationDtoStatusEnum,
} from '../../../apis/client-axios';
import ButtonWrap from '../../../components/ButtonWrap';
import DatePickerWrap from '../../../components/DatePickerWrap';
import TableWrap from '../../../components/TableWrap';
import ToastError from '../../../components/Toast/ToastError';
import ToastSuccess from '../../../components/Toast/ToastSuccess';
import Widget from '../../../components/Widget';
import { CommonStatus, DATE_FORMAT, YEAR_MONTH_DATE_FORMAT_JP } from '../../../constants/common';
import { CONFIRMATION_API } from '../../../constants/QueryKey';
import { RootState } from '../../../store';
import './style.scss';
import { statusMapping } from '../../../utils';

const ApplycationApproval = () => {
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [allChecked, setAllChecked] = useState(false);
  const intl = useIntl();
  const [form] = Form.useForm();
  const [filter, setFilter] = useState<Filter>({ page: 1, pageSize: 20, fullTextSearch: '' });
  const navigate = useNavigate();
  const role = useSelector((state: RootState) => state.auth.authUser?.role);
  const {
    data: applicationData,
    refetch,
    isLoading,
  } = useQuery({
    queryKey: [CONFIRMATION_API, filter],
    queryFn: () =>
      confirmationApi.confirmationControllerGetListApprove(
        filter.date ? dayjs(filter.date).format(DATE_FORMAT) : dayjs().format(DATE_FORMAT),
        filter.date ? dayjs(filter.date).format(DATE_FORMAT) : dayjs().format(DATE_FORMAT),
        filter.page,
        undefined,
        undefined,
        true,
        filter.pageSize,
        undefined,
        filter.fullTextSearch,
      ),
    enabled: !!filter,
  });
  useEffect(() => {
    if (role && role !== EmployeeRelatedInformationDtoRolesEnum.Manager) {
      navigate(-1);
    }
  }, [navigate, role]);

  const updateOneConfirmMutation = useMutation({
    mutationFn: ({ payload }: { payload: { id: string; updateConfirmationDto: UpdateConfirmationDto } }) =>
      confirmationApi.confirmationControllerUpdate(payload.id, payload.updateConfirmationDto),
    onSuccess: (data: any) => {
      refetch();
      setAllChecked(false);
      setSelectedRowKeys([]);

      if (data?.data?.data?.status === CommonStatus.REFUSED) {
        ToastSuccess({
          content: intl.formatMessage({ id: 'application.approval.notice.deny.success' }),
        });
      } else {
        ToastSuccess({
          content: intl.formatMessage({ id: 'application.approval.notice.success' }),
        });
      }
    },
    onError: (error: any) => {
      const errorCodeInvalidOption = [
        'CHECKOUT_AFTER_WORK_START',
        'ATTENDANCE_CORRECTION_EXPIRED',
        'CHECKOUT_BEFORE_CHECKIN_APPROVAL',
      ];

      const errorCode = error?.response?.data?.errorCode;

      if (errorCodeInvalidOption.includes(errorCode)) {
        return ToastError({
          content: intl.formatMessage({ id: `attendance.confirm.noti.${errorCode}.approval` }),
        });
      }

      return ToastError({
        content: intl.formatMessage({ id: 'attendance.confirm.noti.fail' }),
      });
    },
  });
  const updateConfirmMutation = useMutation({
    mutationFn: (payload: BatchUpdateConfirmationDto) => confirmationApi.confirmationControllerBatchUpdate(payload),
    onSuccess: () => {
      refetch();
      setAllChecked(false);
      setSelectedRowKeys([]);
      ToastSuccess({
        content: intl.formatMessage({ id: 'attendance.confirm.noti.success' }),
      });
    },
    onError: () => {
      ToastError({
        content: intl.formatMessage({ id: 'attendance.confirm.noti.fail' }),
      });
    },
  });
  const handleUpdateOne = (id: string, status: UpdateConfirmationDtoStatusEnum) => {
    const payload = {
      payload: {
        id: id,
        updateConfirmationDto: {
          status,
        },
      },
    };
    updateOneConfirmMutation.mutate(payload);
  };
  const handleUpdate = (payload: BatchUpdateConfirmationDto) => {
    if (payload.ids.length === 0) {
      return;
    } else {
      updateConfirmMutation.mutate(payload);
    }
  };

  const handleTitleCheckboxChange = (e) => {
    const isChecked = e.target.checked;

    const pendingRowKeys =
      applicationData?.data.data?.content
        ?.filter((item) => item.status === CommonStatus.PENDING)
        .map((item) => item.id) || [];

    if (pendingRowKeys.length === 0) {
      setAllChecked(false);
      setSelectedRowKeys([]);
      return;
    }

    setAllChecked(isChecked);

    if (isChecked) {
      setSelectedRowKeys(pendingRowKeys);
    } else {
      setSelectedRowKeys([]);
    }
  };

  const handleRowCheckboxChange = (rowId, rowStatus) => {
    if (rowStatus !== CommonStatus.PENDING) return;

    const newSelectedRowKeys = selectedRowKeys.includes(rowId)
      ? selectedRowKeys.filter((key) => key !== rowId)
      : [...selectedRowKeys, rowId];
    setSelectedRowKeys(newSelectedRowKeys);
    setAllChecked(newSelectedRowKeys.length === applicationData?.data.data?.content?.length);
  };
  const columns = [
    {
      title: (
        <Form.Item name={'title-checkbox'}>
          <Checkbox
            checked={
              selectedRowKeys.length > 0 &&
              selectedRowKeys.length ===
                Number(
                  applicationData?.data.data?.content?.filter((item) => item.status === CommonStatus.PENDING).length,
                )
            }
            indeterminate={
              selectedRowKeys.length > 0 &&
              selectedRowKeys.length <
                Number(
                  applicationData?.data.data?.content?.filter((item) => item.status === CommonStatus.PENDING).length,
                )
            }
            // className={
            //   selectedRowKeys.length > 0 &&
            //   selectedRowKeys.length ===
            //     applicationData?.data.data?.content?.filter((item) => item.status === CommonStatus.PENDING).length
            //     ? 'ant-checkbox-checked'
            //     : ''
            // }
            onChange={handleTitleCheckboxChange}
            disabled={
              applicationData?.data.data?.content?.filter((item) => item.status === CommonStatus.PENDING).length === 0
            }
          />
        </Form.Item>
      ),
      dataIndex: 'checkbox',
      key: 'checkbox',
      render: (record, row) => {
        const isPending = row?.status === CommonStatus.PENDING;
        return (
          <Form.Item name={'row-checkbox'}>
            <Checkbox
              disabled={!isPending}
              checked={isPending && selectedRowKeys.includes(row.id)}
              onChange={() => handleRowCheckboxChange(row.id, row.status)}
            />
          </Form.Item>
        );
      },
    },
    {
      title: intl.formatMessage({ id: 'application.approval.column.applicant' }),
      dataIndex: 'user',
      key: 'user',
      render: (record) => {
        return <p className="applycation-approval_table-column-data">{record?.name}</p>;
      },
    },
    {
      title: intl.formatMessage({ id: 'application.approval.column.distinction' }),
      dataIndex: 'title',
      key: 'title',
      render: (record) => {
        return <p className="applycation-approval_table-column-data">{record}</p>;
      },
    },
    {
      title: intl.formatMessage({ id: 'application.approval.column.notes' }),
      dataIndex: 'notes',
      key: 'notes',
      render: (_, record) => {
        return <p className="applycation-approval_table-column-data">{record?.notes}</p>;
      },
    },
    {
      title: intl.formatMessage({ id: 'application.approval.column.time' }),
      dataIndex: 'timeRequest',
      key: 'timeRequest',
      render: (record) => {
        return <p className="applycation-approval_table-column-data">{record}</p>;
      },
    },
    {
      title: intl.formatMessage({ id: 'application.approval.column.status' }),
      dataIndex: 'status',
      key: 'status',
      render: (record) => {
        const { class: statusClass, intlText } = statusMapping[record] || statusMapping.default;
        const statusText = intlText ? intl.formatMessage({ id: intlText }) : '';
        return <p className={`applycation-approval_table-column-${statusClass}`}>{statusText}</p>;
      },
    },
    {
      title: '',
      dataIndex: 'option',
      key: 'option',
      render: (record, row) => {
        return (
          <div className="applycation-approval_table-column-button">
            <ButtonWrap
              onClick={() => handleUpdateOne(row.id, UpdateConfirmationDtoStatusEnum.NUMBER_MINUS_1)}
              disabled={row?.status !== CommonStatus.PENDING || updateOneConfirmMutation.isPending}
              className="applycation-approval_table-column-button-deny"
            >
              {intl.formatMessage({ id: 'application.approval.button.deny' })}
            </ButtonWrap>
            <ButtonWrap
              onClick={() => handleUpdateOne(row.id, UpdateConfirmationDtoStatusEnum.NUMBER_1)}
              disabled={row?.status !== CommonStatus.PENDING || updateOneConfirmMutation.isPending}
              className="applycation-approval_table-column-button-admit"
            >
              {intl.formatMessage({ id: 'application.approval.button.admit' })}
            </ButtonWrap>
          </div>
        );
      },
    },
  ];
  useEffect(() => {
    if (allChecked && applicationData && filter.date) {
      const pendingRowKeys =
        applicationData.data.data?.content
          ?.filter((item) => item.status === CommonStatus.PENDING)
          .map((item) => item.id) || [];
      setSelectedRowKeys(pendingRowKeys);
      setAllChecked(
        pendingRowKeys.length > 0 && pendingRowKeys.length === applicationData?.data?.data?.content?.length,
      );
    }
  }, [applicationData, filter.date, allChecked]);

  return (
    <div className="applycation-approval">
      <Widget
        breadcrumbs={[intl.formatMessage({ id: 'application.approval.breadcumb' })]}
        content={
          <Form className="applycation-approval_form" form={form} layout="vertical">
            <TableWrap
              isPagination
              loading={isLoading}
              onSearch={(search) => {
                setFilter((prevFilters) => ({
                  ...prevFilters,
                  page: 1,
                  fullTextSearch: search,
                }));
              }}
              tableProps={{
                dataSource: applicationData?.data.data?.content || [],
                columns: columns,
                className: 'applycation-approval_table',
              }}
              paginationProps={{
                current: applicationData?.data?.data?.page,
                pageSize: applicationData?.data?.data?.size,
                total: applicationData?.data?.data?.total,
                onChange: (page, pageSize) =>
                  setFilter((prevFilters) => ({
                    ...prevFilters,
                    page,
                    pageSize,
                  })),
              }}
              rightTopContent={
                <div className="applycation-approval_table-rightContent">
                  <Form.Item name={'time'} className="mb-0">
                    <DatePickerWrap
                      allowClear={false}
                      onChange={(date) => {
                        setFilter((prevFilters) => ({
                          ...prevFilters,
                          page: 1,
                          date,
                        }));
                        refetch();
                      }}
                      defaultValue={dayjs()}
                      placement="topLeft"
                      format={YEAR_MONTH_DATE_FORMAT_JP}
                      width={312}
                    />
                  </Form.Item>
                  {selectedRowKeys.length > 0 ? (
                    <div className="applycation-approval_table-selected">
                      <span>{intl.formatMessage({ id: 'application.approval.selected' })}</span>
                      <ButtonWrap
                        onClick={() =>
                          handleUpdate({
                            ids: selectedRowKeys.map(String),
                            status: BatchUpdateConfirmationDtoStatusEnum.NUMBER_MINUS_1,
                          })
                        }
                        className="applycation-approval_table-column-button-deny"
                        disabled={updateConfirmMutation.isPending}
                      >
                        {intl.formatMessage({ id: 'application.approval.button.deny' })}
                      </ButtonWrap>
                      <ButtonWrap
                        onClick={() =>
                          handleUpdate({
                            ids: selectedRowKeys.map(String),
                            status: BatchUpdateConfirmationDtoStatusEnum.NUMBER_1,
                          })
                        }
                        className="applycation-approval_table-column-button-admit"
                        disabled={updateConfirmMutation.isPending}
                      >
                        {intl.formatMessage({ id: 'application.approval.button.admit' })}
                      </ButtonWrap>
                    </div>
                  ) : (
                    <></>
                  )}
                </div>
              }
            />
          </Form>
        }
        title={intl.formatMessage({ id: 'application.approval.title' })}
      />
    </div>
  );
};
export default ApplycationApproval;
